import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useOverlayA11y } from '../hooks/useOverlayA11y'
import { RiskBadge, MatchBandBadge, SessionRail } from './ui'
import type { MatchResult } from '../lib/types'

interface TimelineData {
  case: { id: string; status: string; session_count: number; reached_session_4: boolean; opened_at: string | null; match_id: string | null }
  session_events: Array<{ id: string; status: string; session_number: number; scheduled_at: string | null; created_at: string }>
  alerts: Array<{
    id: string; risk_band: string; reason: string; recommended_action: string; status: string; created_at: string
    continuity_score: number | null; continuity_label: string | null; recommended_interventions: string[]
  }>
  interventions: Array<{ id: string; intervention_type: string; notes: string | null; outcome: string | null; created_at: string }>
}

const TERMINAL_STATUSES = ['completed', 'inactive', 'rerouted']

// scheduled_at is built from the provider's work_start_hour/work_end_hour as
// literal wall-clock numbers stamped with a UTC tzinfo (no provider timezone
// concept exists yet — see BookAppointment.tsx) — must stay pinned to UTC so
// the hour the provider configured round-trips correctly. created_at (the
// fallback when a session was recorded without a real scheduled time) is a
// genuine absolute timestamp and should display in the viewer's local time.
function sessionTimeLabel(scheduledAt: string | null, createdAt: string): string {
  if (scheduledAt) return new Date(scheduledAt).toLocaleString(undefined, { timeZone: 'UTC' })
  return new Date(createdAt).toLocaleString()
}

export function CaseDrawer({ caseId, onClose, providerName, patientName }: {
  caseId: string
  onClose: () => void
  providerName?: string
  patientName?: string
}) {
  const [data, setData] = useState<TimelineData | null>(null)
  const [match, setMatch] = useState<MatchResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState('')
  const [interventionType, setInterventionType] = useState('outreach_call')
  const [sessionStatus, setSessionStatus] = useState('attended')
  const [recording, setRecording] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const drawerRef = useOverlayA11y(onClose)

  async function load() {
    setLoading(true)
    const result: TimelineData = await api(`/v1/cases/${caseId}/timeline`)
    setData(result)
    if (result.case.match_id) {
      try {
        const m = await api(`/v1/matches/${result.case.match_id}`)
        setMatch(m)
      } catch {
        setMatch(null)
      }
    } else {
      setMatch(null)
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [caseId])

  async function recordSession() {
    setRecording(true)
    try {
      await api('/v1/session-events', {
        method: 'POST',
        body: JSON.stringify({
          case_id: caseId,
          status: sessionStatus,
          scheduled_at: new Date().toISOString(),
          source: 'manual',
        }),
      })
      load()
    } finally {
      setRecording(false)
    }
  }

  async function logIntervention(alertId?: string) {
    await api('/v1/interventions', {
      method: 'POST',
      body: JSON.stringify({ case_id: caseId, alert_id: alertId, intervention_type: interventionType, notes: note || null }),
    })
    setNote('')
    load()
  }

  async function changeCaseStatus(status: 'completed' | 'inactive' | 'rerouted') {
    const labels: Record<string, string> = { completed: 'mark this case completed', inactive: 'mark this case inactive', rerouted: 'reroute this case' }
    if (!window.confirm(`Are you sure you want to ${labels[status]}? This cannot be undone.`)) return
    setStatusBusy(true)
    try {
      await api(`/v1/cases/${caseId}`, { method: 'PATCH', body: JSON.stringify({ status }) })
      load()
    } finally {
      setStatusBusy(false)
    }
  }

  return (
    <div className="drawer-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="drawer" ref={drawerRef} role="dialog" aria-modal="true" aria-label={`Case details for ${patientName || 'this case'}`} tabIndex={-1}>
        <button className="drawer-close" onClick={onClose}>close ×</button>
        <h2>{patientName || 'Case'}</h2>
        {providerName && <div className="muted" style={{ marginBottom: 10 }}>Provider: {providerName}</div>}

        {loading && <div className="page-loading">Loading…</div>}
        {data && (
          <>
            <SessionRail count={data.case.session_count} />
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
              Status: {data.case.reached_session_4 ? 'retained' : data.case.status}
            </div>

            {match && (
              <div className="section-block">
                <div className="section-head"><h2>Why this match</h2></div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                  <MatchBandBadge band={match.match_band} />
                  <RiskBadge band={match.risk_band} />
                </div>
                <div className="chip-list">
                  {match.match_reasons.map((r, i) => <span key={i} className="chip">{r}</span>)}
                </div>
              </div>
            )}

            {data.case.status === 'active' && (
              <div className="section-block">
                <div className="section-head"><h2>Record a session</h2></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="inp" value={sessionStatus} onChange={(e) => setSessionStatus(e.target.value)}>
                    <option value="attended">Attended</option>
                    <option value="no_show">No-show</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="provider_cancelled">Provider cancelled</option>
                    <option value="patient_cancelled">Patient cancelled</option>
                    <option value="rescheduled">Rescheduled</option>
                  </select>
                  <button className="btn small" onClick={recordSession} disabled={recording}>
                    {recording ? 'Saving…' : 'Log'}
                  </button>
                </div>
              </div>
            )}

            <div className="section-block">
              <div className="section-head"><h2>Open risk alerts</h2></div>
              {data.alerts.filter((a) => a.status === 'open').length === 0 && (
                <div className="empty">No open continuity alerts for this case.</div>
              )}
              {data.alerts.filter((a) => a.status === 'open').map((a) => (
                <div key={a.id} className="card" style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <RiskBadge band={a.risk_band} />
                    {a.continuity_score != null && (
                      <span className="muted" style={{ fontSize: 12.5 }}>
                        Continuity: {a.continuity_score}/100{a.continuity_label ? ` (${a.continuity_label})` : ''}
                      </span>
                    )}
                  </div>
                  <p style={{ marginTop: 8 }}>{a.reason}</p>
                  <p className="muted" style={{ fontSize: 13 }}>{a.recommended_action}</p>
                  <button className="btn small" onClick={() => logIntervention(a.id)}>Log intervention</button>
                </div>
              ))}
            </div>

            <div className="section-block">
              <div className="section-head"><h2>Log an intervention</h2></div>
              {(() => {
                // Highlight the highest-severity open alert's recommended
                // interventions as their own optgroup instead of showing 5
                // undifferentiated options — see continuity_config.py's
                // RECOMMENDED_INTERVENTIONS_BY_BAND.
                const openAlerts = data.alerts.filter((al) => al.status === 'open')
                const bandRank: Record<string, number> = { critical: 0, high: 1, moderate: 2, low: 3 }
                const topAlert = [...openAlerts].sort((x, y) => (bandRank[x.risk_band] ?? 9) - (bandRank[y.risk_band] ?? 9))[0]
                const recommended = new Set(topAlert?.recommended_interventions || [])
                const options: Array<[string, string]> = [
                  ['outreach_call', 'Outreach call'],
                  ['outreach_message', 'Outreach message'],
                  ['reschedule_assist', 'Reschedule assist'],
                  ['provider_check_in', 'Provider check-in'],
                  ['reroute_offer', 'Offer reroute'],
                ]
                const recommendedOptions = options.filter(([value]) => recommended.has(value))
                const otherOptions = options.filter(([value]) => !recommended.has(value))
                return (
                  <select className="inp" value={interventionType} onChange={(e) => setInterventionType(e.target.value)} style={{ marginBottom: 8 }}>
                    {recommendedOptions.length > 0 && (
                      <optgroup label="Recommended for this risk level">
                        {recommendedOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </optgroup>
                    )}
                    <optgroup label={recommendedOptions.length > 0 ? 'Other options' : 'Intervention type'}>
                      {otherOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </optgroup>
                  </select>
                )
              })()}
              <textarea className="inp" placeholder="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={{ width: '100%', marginBottom: 8 }} />
              <button className="btn small" onClick={() => logIntervention()}>Log intervention</button>
            </div>

            <div className="section-block">
              <div className="section-head"><h2>Case status</h2></div>
              {data.case.status === 'active' ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn small outline" disabled={statusBusy} onClick={() => changeCaseStatus('completed')}>Mark completed</button>
                  <button className="btn small outline" disabled={statusBusy} onClick={() => changeCaseStatus('inactive')}>Mark inactive</button>
                  <button className="btn small outline" disabled={statusBusy} onClick={() => changeCaseStatus('rerouted')}>Reroute</button>
                </div>
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>
                  This case is <b>{data.case.status}</b> — status is final and cannot change again.
                </p>
              )}
            </div>

            <div className="section-block">
              <div className="section-head"><h2>Case timeline</h2></div>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="ti-type">Case opened</div>
                  <div className="ti-time">{data.case.opened_at ? new Date(data.case.opened_at).toLocaleString() : '—'}</div>
                </div>
                {data.session_events.map((e) => (
                  <div className="timeline-item" key={e.id}>
                    <div className="ti-type">Session {e.session_number}: {e.status}</div>
                    <div className="ti-time">{sessionTimeLabel(e.scheduled_at, e.created_at)}</div>
                  </div>
                ))}
                {data.interventions.map((iv) => (
                  <div className="timeline-item" key={iv.id}>
                    <div className="ti-type">Intervention: {iv.intervention_type}{iv.outcome ? ` → ${iv.outcome}` : ''}</div>
                    <div className="ti-time">{new Date(iv.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {TERMINAL_STATUSES.includes(data.case.status) && (
                  <div className="timeline-item">
                    <div className="ti-type">Case {data.case.status}</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
